/**
 * What the quote form sends, what it refuses, and what the server's refusals mean — `W4-T10` §2.5.
 *
 * Pure, for `features/jobs/posting.ts`'s reason: the route action is plumbing, and the rules are
 * here where they can be read in one place. Keys, never sentences.
 */
import { QUOTE_MIN_AMOUNT_CENTS, type QuoteInput } from '@marketplace/contracts';
import { ApiError } from '../../shared/api-error.js';
import { eurosToCents } from '../jobs/posting.js';

export type QuoteFormField = 'amount' | 'validUntil' | 'breakdown';

export type QuoteFormResult =
  { ok: true; input: QuoteInput } | { ok: false; errors: Partial<Record<QuoteFormField, string>> };

/** How long a quote stays good unless the professional says otherwise. */
export const DEFAULT_VALIDITY_DAYS = 14;

/** `YYYY-MM-DD`, which is what a date input holds, `days` from `now`. */
export function dateInputValue(now: Date, days: number): string {
  const then = new Date(now.getTime() + days * 86_400_000);
  return then.toISOString().slice(0, 10);
}

/**
 * The chosen day, to its last millisecond in UTC.
 *
 * A date input has no time, and *valid until the 15th* means through the 15th — sending midnight
 * would expire the quote the moment the day it names begins.
 */
export function endOfDay(date: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  const parsed = new Date(`${date}T23:59:59.999Z`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

export function parseQuoteForm(form: FormData, now: Date = new Date()): QuoteFormResult {
  const errors: Partial<Record<QuoteFormField, string>> = {};

  const amountTyped = text(form, 'amount');
  const amountCents = amountTyped === '' ? undefined : eurosToCents(amountTyped);
  if (amountTyped === '') errors.amount = 'quoteForm.error.amount';
  else if (amountCents === undefined) errors.amount = 'quoteForm.error.amountFormat';
  // `W4-T16`: a courtesy, with the contract's own constant — the server's floor is the one that counts.
  else if (amountCents < QUOTE_MIN_AMOUNT_CENTS) errors.amount = 'quoteForm.error.amountMin';

  const validUntil = endOfDay(text(form, 'validUntil'));
  if (validUntil === undefined || new Date(validUntil) <= now) {
    errors.validUntil = 'quoteForm.error.validUntil';
  }

  const breakdown = text(form, 'breakdown');
  if (breakdown.length > 5000) errors.breakdown = 'quoteForm.error.breakdownLong';

  if (amountCents === undefined || validUntil === undefined || Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    input: { amountCents, validUntil, ...(breakdown === '' ? {} : { breakdown }) },
  };
}

/**
 * A refusal, as the sentence the provider reads — keyed on the **code**, never the English message.
 *
 * `409 CONFLICT` covers both "you already have an active quote" and "your profile is unfinished",
 * and the screen does not guess which (spec §2.5): the sentence names both.
 *
 * `400 VALIDATION_FAILED` is the floor (`W4-T16` AC16). `parseQuoteForm` checks every field the
 * server checks before anything is sent, so the one refusal this form can still meet is a floor that
 * moved under a stale bundle — and the sentence for it is the minimum, not "please try again".
 */
export function quoteFailureKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'quoteForm.error.failed';
  if (error.status === 400 && error.message === 'VALIDATION_FAILED') {
    return 'quoteForm.error.amountMin';
  }
  if (error.status === 409 && error.message === 'JOB_CLOSED') return 'quoteForm.error.closed';
  if (error.status === 404) return 'quoteForm.error.gone';
  if (error.status === 409) return 'quoteForm.error.conflict';
  return 'quoteForm.error.failed';
}
